import type React from 'react'
import { useIntl } from 'react-intl'

import { Link } from '@tanstack/react-router'
import { AnimatePresence } from 'motion/react'
import { type Except } from 'type-fest'

import { Button } from '@/components/atoms/Button/Button.tsx'
import { Car, type CarProps } from '@/components/atoms/Car'
import { Icons } from '@/components/atoms/Icons/Icons'
import { BroadcastMessageDialog } from '@/components/molecules/BroadcastMessageDialog'
import { TeamMessageCard, type TeamMessageCardProps } from '@/components/molecules/TeamMessageCard'
import { TeamMessagesStatusCard } from '@/components/molecules/TeamMessagesStatusCard/TeamMessagesStatusCard'
import { VoiceRecorderPanel } from '@/components/molecules/VoiceRecorderPanel'
import { type MessagingRailProps } from '@/components/organisms/MessagingRail'
import { MessagingShell } from '@/components/templates/MessagingShell'
import { useVoiceRecorder } from '@/hooks/useVoiceRecorder'
import { cn } from '@/lib/utils'
import { VoicePlaybackProvider } from '@/model/contexts/VoicePlaybackContext'

import { TeamMessageForm } from './TeamMessageForm'
import { TeamMessageFormProvider } from './TeamMessageFormProvider'

export interface TeamMessage extends Except<TeamMessageCardProps, 'regionName'> {
  _id: string
}

export interface TeamItem extends Pick<CarProps, 'state' | 'carNumber' | 'carType'> {
  _id: string
  count?: number
  hasUnreadMessages: boolean
}

export interface TeamLinkConfig {
  to: string
  params: (teamId: string) => Record<string, string>
  search?: Record<string, unknown>
}

export interface TeamConversationViewProps {
  rail: MessagingRailProps
  teams: TeamItem[]
  selectedTeam?: string
  messages: TeamMessage[]
  regionName: string
  piloteRegionId: number
  onConfirmAsRead?: () => void
  onSendMessage?: (message: string) => Promise<void> | void
  onSendVoiceMessage?: (blob: Blob, durationMs: number) => Promise<void>
  onBroadcastMessage?: (message: string) => Promise<void>
  onBroadcastVoiceMessage?: (blob: Blob, durationMs: number) => Promise<void>
  teamLinkConfig?: TeamLinkConfig
  readOnly?: boolean
  readOnlyDate?: string
}

type TeamConversationComposerProps = Pick<
  TeamConversationViewProps,
  'regionName' | 'onConfirmAsRead' | 'onSendMessage' | 'onSendVoiceMessage'
>

const TeamConversationComposer: React.FC<TeamConversationComposerProps> = ({
  regionName,
  onConfirmAsRead,
  onSendMessage,
  onSendVoiceMessage,
}) => {
  const intl = useIntl()
  // Held above the form, which a message from the team swaps for the read confirmation: the operator's recording stays
  // on screen next to it, and its clip goes out once the message is confirmed.
  const recorder = useVoiceRecorder()

  if (!onSendMessage && !onConfirmAsRead) return null

  // The provider outlives that swap too, so the text the operator was typing comes back with the form.
  return (
    <TeamMessageFormProvider onSendMessage={onSendMessage ?? noop}>
      {onSendMessage ? (
        <TeamMessageForm
          regionName={regionName}
          voice={onSendVoiceMessage && { recorder, onSend: onSendVoiceMessage }}
        />
      ) : (
        <div className="space-y-4 p-6 pb-24 pl-24">
          {recorder.state.status !== 'idle' && <VoiceRecorderPanel recorder={recorder} />}
          <Button onClick={onConfirmAsRead}>{intl.formatMessage({ id: 'message.action.confirmAsRead' })}</Button>
        </div>
      )}
    </TeamMessageFormProvider>
  )
}

const noop = () => Promise.resolve()

export const TeamConversationView: React.FC<TeamConversationViewProps> = ({
  rail,
  teams = [],
  selectedTeam,
  messages,
  regionName,
  piloteRegionId,
  onConfirmAsRead,
  onSendMessage,
  onSendVoiceMessage,
  onBroadcastMessage,
  onBroadcastVoiceMessage,
  teamLinkConfig,
  readOnly,
  readOnlyDate,
}) => {
  const intl = useIntl()

  const selectedTeamInfo = teams.find((t) => t._id === selectedTeam)

  return (
    <VoicePlaybackProvider>
      <MessagingShell
        rail={rail}
        regionName={regionName}
        piloteRegionId={piloteRegionId}
        readOnly={readOnly}
        readOnlyDate={readOnlyDate}>
        <aside className="rounded-corner flex flex-shrink-0 flex-col bg-white">
          {onBroadcastMessage ? (
            <BroadcastMessageDialog onSend={onBroadcastMessage} onSendVoice={onBroadcastVoiceMessage}>
              <button
                type="button"
                className="flex h-12 cursor-pointer items-center gap-2.5 rounded-tl-[20px] rounded-tr-[20px] border-2 border-teal-400 bg-teal-100 px-5 py-2 hover:bg-teal-50">
                <Icons.megaphone className="h-8 w-[30px] text-gray-800" />
                <span className="text-base font-normal text-gray-800">
                  {intl.formatMessage({ id: 'message.broadcast.trigger' })}
                </span>
              </button>
            </BroadcastMessageDialog>
          ) : (
            <div className="flex h-12 items-center justify-center border-b border-gray-400 text-base font-bold text-gray-800">
              {intl.formatMessage({ id: 'message.team.section.teams' })}
            </div>
          )}
          {/* eslint-disable @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-assignment */}
          <ul className="grid h-[calc(100vh-11rem)] min-w-[119px] auto-cols-[minmax(max-content,1fr)] grid-flow-col grid-rows-[repeat(auto-fill,var(--team-row-height))] [--team-row-height:45px]">
            {teams.map((t) => (
              <Link
                key={t._id}
                className="h-(--team-row-height)"
                to={(teamLinkConfig?.to ?? `/dashboard/$regionId/messages/$teamId`) as any}
                from={(teamLinkConfig?.to ?? `/dashboard/$regionId/messages/$teamId`) as any}
                params={(teamLinkConfig?.params(t._id) ?? { teamId: t._id }) as any}
                search={teamLinkConfig?.search as any}
                activeProps={{ className: 'bg-teal-100 border-teal-400 border-2' }}
                inactiveProps={{
                  className: 'hover:bg-gray-100 divide-gray-200 border-b border-r pr-[1px] pb-[1px] pl-[2px] pt-[2px]',
                }}>
                <TeamMessagesStatusCard
                  car={t}
                  className={cn('w-28')}
                  count={t.count}
                  hasUnreadMessages={t.hasUnreadMessages}
                />
              </Link>
            ))}
          </ul>
          {/* eslint-enable @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-assignment */}
        </aside>

        <section
          key={selectedTeam ?? 'none'}
          className="rounded-corner flex flex-1 flex-col justify-stretch overflow-hidden bg-white">
          {selectedTeam && (
            <>
              <div className="flex items-center justify-center gap-2 py-[5px] select-none">
                <p className="text-base font-bold text-gray-800">
                  {intl.formatMessage({ id: 'message.team.section.title' })}
                </p>
                {selectedTeamInfo && <Car {...selectedTeamInfo} />}
              </div>
              <div className="h-3 w-full bg-gray-400" />
              <div className="flex flex-1 flex-col-reverse overflow-y-auto">
                <TeamConversationComposer
                  regionName={regionName}
                  onConfirmAsRead={onConfirmAsRead}
                  onSendMessage={onSendMessage}
                  onSendVoiceMessage={onSendVoiceMessage}
                />
                <AnimatePresence initial={false}>
                  {messages.map(({ _id, ...message }) => (
                    <TeamMessageCard key={_id} {...message} regionName={regionName} piloteRegionId={piloteRegionId} />
                  ))}
                </AnimatePresence>
              </div>
            </>
          )}
        </section>
      </MessagingShell>
    </VoicePlaybackProvider>
  )
}
